import { api } from "@app/api/client";
import type { AuthUser } from "@app/auth/store";

interface LoginTokenResponse {
  token: string;
}

interface MeResponse {
  id: number;
  email: string;
  role?: string;
  firstName?: string;
  lastName?: string;
}

async function fetchUserFromToken(token: string): Promise<AuthUser> {
  const { data: me } = await api.get<MeResponse>("/api/me", {
    headers: { Authorization: `Bearer ${token}` }
  });
  return {
    id: me.id,
    email: me.email,
    role: me.role ?? "user",
    firstName: me.firstName,
    lastName: me.lastName
  };
}

/**
 * Logs in with email/password against the shared backend, then loads the profile.
 * Whether the resulting user may actually use the backoffice is decided by the
 * backend (`/admin/*` returns 403 for non-allowlisted users); this only
 * establishes a session.
 */
export async function loginAndFetchUser(
  email: string,
  password: string
): Promise<{ token: string; user: AuthUser }> {
  const { data: tokenRes } = await api.post<LoginTokenResponse>("/api/auth/login", { email, password });
  const token = tokenRes.token;
  const user = await fetchUserFromToken(token);
  return { token, user };
}

/**
 * Exchanges a Google ID token (from Google Identity Services) for a backend JWT
 * via POST /api/auth/oauth, then loads the profile. Same authorization rules as
 * email/password login: the backoffice still gates on the admin allowlist.
 */
export async function loginWithGoogle(idToken: string): Promise<{ token: string; user: AuthUser }> {
  const { data: tokenRes } = await api.post<LoginTokenResponse>("/api/auth/oauth", {
    provider: "google",
    idToken
  });
  const token = tokenRes.token;
  const user = await fetchUserFromToken(token);
  return { token, user };
}
